import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import { Add as AddIcon, DeleteOutline, EditOutlined as EditIcon } from "@mui/icons-material";
import useApiCall from "../../hooks/useApiCall";
import { INDIGO, SLATE } from "../../constants/Colors";
import { usePageMeta } from "../../contexts/PageMetaContext";
import LoadingSpinner from "../../components/LoadingSpinner";

const COL_HEADER_SX = {
  fontSize: "0.6875rem",
  fontWeight: 600,
  color: SLATE[500],
  textTransform: "uppercase",
  letterSpacing: "0.05em",
};

export default function BusinessUnits() {
  const navigate = useNavigate();
  const currentApp = useSelector((state) => state?.apps?.selectedApp);
  const tokens = useSelector((state) => state?.tokens.value);
  const { makeRequest } = useApiCall();
  const { setPageMeta } = usePageMeta();

  const [businessUnits, setBusinessUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmUnit, setConfirmUnit] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    if (!confirmUnit) return;
    setDeleting(true);
    try {
      await makeRequest(`/v1/apps/${currentApp.id}/business_units/${confirmUnit.id}`, { method: "DELETE", authTokens: tokens });
      setBusinessUnits((prev) => prev.filter((u) => u.id !== confirmUnit.id));
      setConfirmUnit(null);
    } catch {
      // error already toasted by useApiCall
    } finally {
      setDeleting(false);
    }
  };

  useEffect(() => {
    setPageMeta({
      description: "Manage business units and their agent deployments.",
      actions: (
        <Button
          variant="contained"
          startIcon={<AddIcon sx={{ fontSize: "1rem" }} />}
          onClick={() => navigate(`/${currentApp?.url_code}/business-units/new`)}
          sx={{
            textTransform: "none",
            fontSize: "0.875rem",
            fontWeight: 500,
            boxShadow: "none",
            "&:hover": { boxShadow: "none" },
          }}
        >
          Create Business Unit
        </Button>
      ),
    });
    return () => setPageMeta({});
  }, [currentApp?.url_code]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!currentApp?.id) return;
    setLoading(true);
    makeRequest(`/v1/apps/${currentApp.id}/business_units`, { method: "GET", authTokens: tokens })
      .then((data) => setBusinessUnits(data?.business_units || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [currentApp?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return (
      <LoadingSpinner
        message="Loading business units..."
        subMessage="Fetching business units for this client"
      />
    );
  }

  return (
    <Box sx={{ width: "100%" }}>
      <Box
        sx={{
          bgcolor: "#ffffff",
          border: `1px solid ${SLATE[200]}`,
          borderRadius: "16px",
          overflow: "hidden",
        }}
      >
        {/* Table header */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "72px minmax(150px, 1.5fr) 100px 2fr 96px",
            px: 3,
            py: 1.5,
            borderBottom: `1px solid ${SLATE[200]}`,
            bgcolor: SLATE[50],
          }}
        >
          <Typography sx={COL_HEADER_SX}>Logo</Typography>
          <Typography sx={COL_HEADER_SX}>Name</Typography>
          <Typography sx={COL_HEADER_SX}>Agents</Typography>
          <Typography sx={COL_HEADER_SX}>Description</Typography>
          <Typography sx={COL_HEADER_SX}>Actions</Typography>
        </Box>

        {businessUnits.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 10, width: "100%" }}>
            <Typography sx={{ fontSize: "2rem", mb: 1 }}>🏢</Typography>
            <Typography sx={{ fontSize: "1rem", fontWeight: 600, color: SLATE[900], mb: "6px" }}>
              No business units yet
            </Typography>
            <Typography sx={{ fontSize: "0.875rem", color: SLATE[500] }}>
              Create your first business unit to get started
            </Typography>
          </Box>
        ) : (
          businessUnits.map((unit, idx) => (
            <Box
              key={unit.id}
              onClick={() => navigate(`/${currentApp.url_code}/business-units/${unit.id}/agents`)}
              sx={{
                display: "grid",
                gridTemplateColumns: "72px minmax(150px, 1.5fr) 100px 2fr 96px",
                px: 3,
                py: 2,
                alignItems: "center",
                cursor: "pointer",
                borderBottom: idx < businessUnits.length - 1 ? `1px solid ${SLATE[100]}` : "none",
                "&:hover": { bgcolor: SLATE[50] },
              }}
            >
              {/* Logo */}
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: "8px",
                  border: `1px solid ${SLATE[200]}`,
                  bgcolor: SLATE[50],
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  overflow: "hidden",
                }}
              >
                {unit.logo ? (
                  <img src={unit.logo} alt={unit.name} style={{ width: 32, height: 32, objectFit: "contain" }} />
                ) : (
                  <Typography sx={{ fontSize: "0.625rem", color: SLATE[400] }}>No logo</Typography>
                )}
              </Box>

              {/* Name */}
              <Typography sx={{ fontSize: "0.875rem", fontWeight: 500, color: SLATE[900], overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", pr: 2 }}>
                {unit.name}
              </Typography>

              {/* Agents count */}
              <Typography sx={{ fontSize: "0.875rem", color: SLATE[500] }}>
                {unit.agents_count ?? 0} {unit.agents_count === 1 ? "agent" : "agents"}
              </Typography>

              {/* Description */}
              <Typography
                sx={{
                  fontSize: "0.8125rem",
                  color: SLATE[500],
                  pr: 3,
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                }}
              >
                {unit.metadata?.description || "—"}
              </Typography>

              {/* Actions */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Tooltip title="Edit" placement="top">
                  <IconButton
                    size="small"
                    aria-label="Edit"
                    onClick={(e) => { e.stopPropagation(); navigate(`/${currentApp.url_code}/business-units/${unit.id}/edit`); }}
                    sx={{ color: SLATE[400], "&:hover": { color: INDIGO[600], bgcolor: INDIGO[50] } }}
                  >
                    <EditIcon sx={{ fontSize: "1rem" }} />
                  </IconButton>
                </Tooltip>
                <IconButton
                  size="small"
                  aria-label="Delete business unit"
                  onClick={(e) => { e.stopPropagation(); setConfirmUnit(unit); }}
                  sx={{ color: SLATE[400], "&:hover": { color: "#ef4444", bgcolor: "transparent" } }}
                >
                  <DeleteOutline sx={{ fontSize: "1rem" }} />
                </IconButton>
              </Box>
            </Box>
          ))
        )}
      </Box>
      <Dialog open={Boolean(confirmUnit)} onClose={() => !deleting && setConfirmUnit(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: "1rem", fontWeight: 600, color: SLATE[900] }}>
          Delete Business Unit
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: "0.875rem", color: SLATE[600] }}>
            Are you sure you want to delete <strong>{confirmUnit?.name}</strong>? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button
            onClick={() => setConfirmUnit(null)}
            disabled={deleting}
            variant="outlined"
            size="small"
            sx={{ borderColor: SLATE[300], color: SLATE[700], textTransform: "none", "&:hover": { borderColor: SLATE[400], bgcolor: SLATE[50] } }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            disabled={deleting}
            variant="contained"
            size="small"
            sx={{ bgcolor: "#ef4444", textTransform: "none", boxShadow: "none", "&:hover": { bgcolor: "#dc2626", boxShadow: "none" } }}
          >
            {deleting ? <CircularProgress size={14} sx={{ color: "#fff", mr: 1 }} /> : null}
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
